import { NewTaskStepItem } from "@/components/forms/NewTaskStepItem";
import type { Step } from "@/types/task";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

interface TaskFormStepsProps {
  steps: Step[];
  errors: Record<string, string>;
  isViewMode?: boolean;
  onUpdateStep: (stepId: string, field: string, value: string) => void;
  onRemoveStep: (stepId: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onAddStep: () => void;
}

export default function TaskFormSteps({
  steps,
  errors,
  onUpdateStep,
  onRemoveStep,
  onDragEnd,
  onAddStep,
  isViewMode = false,
}: TaskFormStepsProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Use steps prop directly for both editing and creating, sorted by sequence
  const currentSteps = (steps || []).sort((a, b) => {
    // Sort by sequence first, then by step_order as fallback
    const aOrder = a.sequence || a.step_order || 0;
    const bOrder = b.sequence || b.step_order || 0;
    return aOrder - bOrder;
  });
  const hasSteps = currentSteps.length > 0;

  return (
    <div className="space-y-4">
      {!hasSteps ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">No steps added yet</p>
          <p className="text-xs">Click "+ New Step" to get started</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={currentSteps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {currentSteps.map((step, index) => (
                <NewTaskStepItem
                  key={step.id}
                  step={{
                    tempId: step.id, // Use step.id as tempId for consistency with NewTaskStepItem
                    step_name: step.step_name,
                    description: step.description || "",
                    assignee_type: step.assignee_type || "User",
                    assignee: step.assignee || "",
                    reference_link: step.reference_link || "",
                    survey_link: step.survey_link || "",
                    status_id: step.status_id || "",
                    is_completed: step.is_completed || false,
                    completed_at: step.completed_at || "",
                    completed_by: step.completed_by || "",
                  }}
                  onUpdate={onUpdateStep}
                  onRemove={onRemoveStep}
                  isViewMode={isViewMode}
                  hasError={!!errors[`step_${index}_name`]}
                  errorMessage={errors[`step_${index}_name`]}
                  initialExpanded={
                    (step as unknown as { is_expanded: boolean }).is_expanded ??
                    false
                  } // New steps expanded, existing steps collapsed
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* New Step Button */}
      {!isViewMode && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onAddStep}
            className="px-6 py-3 border cursor-pointer border-[#374795] rounded-[10px] text-[#374795] text-[14px] font-semibold hover:bg-blue-50 transition-colors"
          >
            New Step
          </button>
        </div>
      )}
    </div>
  );
}
